import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './DatePicker.css';

export interface DatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  placeholder?: string;
  min?: string; // YYYY-MM-DD
  max?: string; // YYYY-MM-DD
  disabled?: boolean;
  id?: string;
  className?: string;
  label?: string;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYMD(s: string): { y: number; m: number; d: number } | null {
  if (!s || s.length < 10) return null;
  const y = parseInt(s.slice(0, 4), 10);
  const m = parseInt(s.slice(5, 7), 10) - 1;
  const d = parseInt(s.slice(8, 10), 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
  return { y, m, d };
}

function isSameDay(a: string, b: string): boolean {
  return a === b;
}

function isBefore(a: string, b: string): boolean {
  return a < b;
}

function isAfter(a: string, b: string): boolean {
  return a > b;
}

function buildYearOptions(min?: string, max?: string, anchorYear?: number): number[] {
  const now = new Date().getFullYear();
  let start = now - 30;
  let end = now + 5;

  const minParsed = min ? parseYMD(min) : null;
  const maxParsed = max ? parseYMD(max) : null;
  if (minParsed) start = Math.min(start, minParsed.y);
  if (maxParsed) end = Math.max(end, maxParsed.y);
  if (anchorYear != null) {
    start = Math.min(start, anchorYear);
    end = Math.max(end, anchorYear);
  }

  const years: number[] = [];
  for (let y = start; y <= end; y += 1) {
    years.push(y);
  }
  return years;
}

const DROPDOWN_GAP = 6;
const DROPDOWN_WIDTH = 280;
const DROPDOWN_MAX_HEIGHT = 440;

type DropdownLayout = {
  top: number;
  left: number;
  maxHeight: number;
  overflowY: 'auto' | 'hidden';
};

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date',
  min,
  max,
  disabled = false,
  id,
  className = '',
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownLayout, setDropdownLayout] = useState<DropdownLayout | null>(null);
  const [viewDate, setViewDate] = useState(() => {
    const p = parseYMD(value);
    if (p) return { year: p.y, month: p.m };
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) {
      setDropdownLayout(null);
      return;
    }

    const updateLayout = () => {
      const trigger = triggerRef.current;
      const dropdown = dropdownRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewH = window.innerHeight;
      const viewW = window.innerWidth;
      const dropdownWidth = Math.min(DROPDOWN_WIDTH, viewW - 24);

      const spaceBelow = viewH - rect.bottom - DROPDOWN_GAP;
      const spaceAbove = rect.top - DROPDOWN_GAP;
      const openDown = spaceBelow >= spaceAbove;

      const contentHeight = dropdown?.scrollHeight ?? DROPDOWN_MAX_HEIGHT;
      const available = (openDown ? spaceBelow : spaceAbove) - 12;
      const maxHeight = Math.max(220, Math.min(DROPDOWN_MAX_HEIGHT, available));

      const placedHeight = Math.min(contentHeight, maxHeight);
      const top = openDown
        ? rect.bottom + DROPDOWN_GAP
        : Math.max(DROPDOWN_GAP, rect.top - placedHeight);

      let left = rect.left;
      if (left + dropdownWidth > viewW - 12) left = viewW - dropdownWidth - 12;
      if (left < 12) left = 12;

      const overflowY = contentHeight > maxHeight + 1 ? 'auto' : 'hidden';

      setDropdownLayout({ top, left, maxHeight, overflowY });
    };

    updateLayout();
    const raf = requestAnimationFrame(updateLayout);

    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
    };
  }, [open, viewDate.year, viewDate.month]);

  useEffect(() => {
    const p = parseYMD(value);
    if (p) setViewDate({ year: p.y, month: p.m });
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const displayText = value
    ? (() => {
        const p = parseYMD(value);
        if (!p) return value;
        return new Date(p.y, p.m, p.d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      })()
    : placeholder;

  const daysInMonth = getDaysInMonth(viewDate.year, viewDate.month);
  const firstDay = getFirstDayOfMonth(viewDate.year, viewDate.month);
  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const handleSelect = (day: number) => {
    const ymd = toYMD(new Date(viewDate.year, viewDate.month, day));
    if (min && isBefore(ymd, min)) return;
    if (max && isAfter(ymd, max)) return;
    onChange(ymd);
    setOpen(false);
  };

  const prevMonth = () => {
    setViewDate((v) => (v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 }));
  };

  const nextMonth = () => {
    setViewDate((v) => (v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 }));
  };

  const goToday = () => {
    const t = new Date();
    setViewDate({ year: t.getFullYear(), month: t.getMonth() });
    onChange(toYMD(t));
    setOpen(false);
  };

  const valueYear = parseYMD(value)?.y;
  const yearOptions = buildYearOptions(min, max, valueYear ?? viewDate.year);

  const setViewMonth = (monthIndex: number) => {
    if (monthIndex < 0 || monthIndex > 11) return;
    setViewDate((v) => ({ ...v, month: monthIndex }));
  };

  const setViewYear = (year: number) => {
    if (!Number.isFinite(year)) return;
    setViewDate((v) => ({ ...v, year }));
  };

  const shiftYear = (delta: number) => {
    setViewDate((v) => {
      const nextYear = v.year + delta;
      if (yearOptions.length > 0) {
        const minY = yearOptions[0];
        const maxY = yearOptions[yearOptions.length - 1];
        if (nextYear < minY || nextYear > maxY) return v;
      }
      return { ...v, year: nextYear };
    });
  };

  return (
    <div ref={rootRef} className={`custom-datepicker ${className}`} id={id}>
      {label && <label className="custom-datepicker-label">{label}</label>}
      <button
        ref={triggerRef}
        type="button"
        className="custom-datepicker-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <i className="fas fa-calendar-alt custom-datepicker-icon"></i>
        <span className={value ? 'custom-datepicker-value' : 'custom-datepicker-placeholder'}>{displayText}</span>
        <i className={`fas fa-chevron-down custom-datepicker-chevron ${open ? 'open' : ''}`}></i>
      </button>

      {open && (() => {
        const content = (
          <div
            ref={dropdownRef}
            className="custom-datepicker-dropdown custom-datepicker-dropdown--positioned"
            role="dialog"
            aria-label="Choose date"
            style={
              dropdownLayout
                ? {
                    position: 'fixed',
                    top: dropdownLayout.top,
                    left: dropdownLayout.left,
                    maxHeight: dropdownLayout.maxHeight,
                    overflowY: dropdownLayout.overflowY,
                    zIndex: 10050,
                  }
                : { position: 'fixed', zIndex: 10050, visibility: 'hidden' }
            }
          >
            <div className="custom-datepicker-nav">
              <button type="button" className="custom-datepicker-nav-btn" onClick={prevMonth} aria-label="Previous month">
                <i className="fas fa-chevron-left"></i>
              </button>
              <div className="custom-datepicker-nav-selects">
                <select
                  className="custom-datepicker-month-select"
                  value={viewDate.month}
                  onChange={(e) => setViewMonth(Number(e.target.value))}
                  aria-label="Month"
                >
                  {MONTHS.map((monthName, index) => (
                    <option key={monthName} value={index}>
                      {monthName}
                    </option>
                  ))}
                </select>
                <div className="custom-datepicker-year-wrap">
                  <button
                    type="button"
                    className="custom-datepicker-year-shift"
                    onClick={() => shiftYear(-1)}
                    aria-label="Previous year"
                    disabled={yearOptions.length > 0 && viewDate.year <= yearOptions[0]}
                  >
                    <i className="fas fa-chevron-left" aria-hidden />
                  </button>
                  <select
                    className="custom-datepicker-year-select"
                    value={viewDate.year}
                    onChange={(e) => setViewYear(Number(e.target.value))}
                    aria-label="Year"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="custom-datepicker-year-shift"
                    onClick={() => shiftYear(1)}
                    aria-label="Next year"
                    disabled={
                      yearOptions.length > 0 && viewDate.year >= yearOptions[yearOptions.length - 1]
                    }
                  >
                    <i className="fas fa-chevron-right" aria-hidden />
                  </button>
                </div>
              </div>
              <button type="button" className="custom-datepicker-nav-btn" onClick={nextMonth} aria-label="Next month">
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
            <div className="custom-datepicker-weekdays">
              {WEEKDAYS.map((w) => (
                <span key={w} className="custom-datepicker-weekday">{w}</span>
              ))}
            </div>
            <div className="custom-datepicker-days">
              {blanks.map((_, i) => (
                <span key={`b-${i}`} className="custom-datepicker-day custom-datepicker-day--blank" />
              ))}
              {days.map((day) => {
                const ymd = toYMD(new Date(viewDate.year, viewDate.month, day));
                const selected = isSameDay(ymd, value);
                const disabledDay = (min && isBefore(ymd, min)) || (max && isAfter(ymd, max));
                const isToday = ymd === toYMD(new Date());
                return (
                  <button
                    key={day}
                    type="button"
                    className={`custom-datepicker-day ${selected ? 'selected' : ''} ${disabledDay ? 'disabled' : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => !disabledDay && handleSelect(day)}
                    disabled={Boolean(disabledDay)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <div className="custom-datepicker-footer">
              <button type="button" className="custom-datepicker-today-btn" onClick={goToday}>
                Today
              </button>
            </div>
          </div>
        );
        return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
      })()}
    </div>
  );
};

export default DatePicker;
